<script lang="ts">
  import {
    Radio,
    CircleCheck,
    CircleAlert,
    Loader2,
    Plug,
    Play,
    Square,
    Unplug,
    Keyboard,
    Volume2,
    VolumeX,
    HelpCircle,
    Usb,
    Wifi,
    Circle,
    Download,
  } from 'lucide-svelte';
  import { onMount, onDestroy } from 'svelte';
  import init, { smoke } from './lib/dsp/wasm/moshon_dsp.js';
  import { RtlSdrSource, type RtlSdrStatus } from './lib/usb/rtlsdr-source';
  import { RtlTcpSource } from './lib/usb/rtltcp-source';
  import { HackRfSource } from './lib/usb/hackrf-source';
  import { HACKRF_DEFAULT_GAIN, HACKRF_AMP_DB } from './lib/usb/hackrf-protocol';
  import type { ColormapName } from './lib/visualizer/spectrum-waterfall';
  import {
    tuning,
    formatHz,
    MODE_INFO,
    GAIN_STEPS,
  } from './lib/state/tuning.svelte';
  import HotkeyHelp from './lib/ui/HotkeyHelp.svelte';
  import FrequencyEntry from './lib/ui/FrequencyEntry.svelte';
  import VfoDial from './lib/ui/VfoDial.svelte';
  import SpectrumWaterfall from './lib/ui/SpectrumWaterfall.svelte';
  import MemoryChannels from './lib/ui/MemoryChannels.svelte';
  import Onboarding from './lib/ui/Onboarding.svelte';
  import NetworkConnect from './lib/ui/NetworkConnect.svelte';
  import AircraftPanel from './lib/ui/AircraftPanel.svelte';
  import LoraPanel from './lib/ui/LoraPanel.svelte';
  import SweepPanel from './lib/ui/SweepPanel.svelte';
  import { AudioPipeline } from './lib/audio/audio-pipeline';
  import { recorder } from './lib/audio/recorder.svelte';
  import { iqRecorder } from './lib/audio/iq-recorder.svelte';
  import Section from './lib/ui/Section.svelte';
  import { aircraftTracker } from './lib/state/aircraft.svelte';
  import type { AdsbRawFrame } from './lib/dsp/adsb-parser';
  import { readHash, writeHash } from './lib/state/url-hash';
  import { peakDbInChannel, dbToSUnit } from './lib/dsp/smeter';

  // ---- WASM smoke (B1) ----
  type WasmStatus = 'pending' | 'ready' | 'error';
  let wasmStatus = $state<WasmStatus>('pending');
  let wasmError = $state<string | null>(null);
  let smokeResult = $state<number | null>(null);

  // ---- IQ stream config ----
  // Sample rate is picked at connect time and held for the session. Valid
  // values depend on the dongle: RTL-SDR supports up to ~3.2 MS/s, HackRF
  // supports 2-20 MS/s per the official spec
  // (https://hackrf.readthedocs.io/en/latest/hackrf_one.html). The HackRF
  // docs recommend ≥8 MS/s so the MAX2837 baseband filter can adequately
  // attenuate adjacent-channel interference; rates below 8 MS/s are
  // supported but produce poorer alias rejection.
  let sampleRate = $state(2_400_000);
  const FFT_SIZE = 2048;
  const FFT_RATE_HZ = 30;

  const RTL_SAMPLE_RATES = [
    { value: 1_920_000, label: '1.92 MS/s' },
    { value: 2_400_000, label: '2.4 MS/s (default)' },
    { value: 2_880_000, label: '2.88 MS/s' },
  ];
  const HACKRF_SAMPLE_RATES = [
    { value: 2_000_000, label: '2 MS/s (min)' },
    { value: 2_400_000, label: '2.4 MS/s (default)' },
    { value: 4_000_000, label: '4 MS/s' },
    { value: 4_800_000, label: '4.8 MS/s' },
    { value: 6_000_000, label: '6 MS/s' },
    { value: 8_000_000, label: '8 MS/s (recommended floor)' },
    { value: 9_600_000, label: '9.6 MS/s' },
    { value: 10_000_000, label: '10 MS/s' },
    { value: 12_000_000, label: '12 MS/s' },
    { value: 14_000_000, label: '14 MS/s' },
    { value: 16_000_000, label: '16 MS/s' },
    { value: 18_000_000, label: '18 MS/s' },
    { value: 20_000_000, label: '20 MS/s (max)' },
  ];
  // Populated reactively after `inputMode` is declared; can't $derived here
  // because inputMode is declared further down.
  let availableSampleRates = $state<{ value: number; label: string }[]>(RTL_SAMPLE_RATES);

  /** Defensive coercion. Svelte 5's `<select bind:value>` is supposed to
   *  preserve the type from `<option value={r.value}>` but in practice the
   *  DOM round-trip can yield a string — and `device.setSampleRate('2400000')`
   *  blows up in unhelpful ways on the worker side. This helper makes any
   *  downstream code that takes a number safe to call. */
  function asValidRate(v: unknown): number {
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) && n > 0 ? n : 2_400_000;
  }

  // Three sources: RTL-SDR via WebUSB, HackRF One via WebUSB, and
  // rtl_tcp-over-WebSocket (remote bridge). They all expose the same
  // listener surface, so the rest of App.svelte can poll activeSource()
  // without caring which physical link is feeding it.
  const usbSource = new RtlSdrSource();
  const hackrfSource = new HackRfSource();
  const netSource = new RtlTcpSource();
  type InputMode = 'usb' | 'hackrf' | 'network';
  let inputMode = $state<InputMode>('usb');
  let bridgeUrl = $state('http://127.0.0.1:9090');
  let rtlTcpTarget = $state('');

  // HackRF per-stage gain (AMP/LNA/VGA). Defaults match the official docs:
  // RF=off, IF=16, BB=16. Persisted to localStorage so settings survive
  // reloads independent of the unified `tuning.gain`.
  let hrfAmpOn = $state(HACKRF_DEFAULT_GAIN.ampOn);
  let hrfLnaDb = $state(HACKRF_DEFAULT_GAIN.lnaDb);
  let hrfVgaDb = $state(HACKRF_DEFAULT_GAIN.vgaDb);

  // Advanced device settings, applicable across sources. Each is persisted
  // separately in localStorage.
  let advOpen = $state(false);
  let offsetEnabled = $state(false);
  let offsetHz = $state(250_000);
  let ppmCorrection = $state(0);
  let rtlBiasT = $state(false);
  let rtlDirectSampling = $state<'off' | 'i' | 'q'>('off');
  let hrfAntennaPower = $state(false);

  // WFM audio de-emphasis time constant. 50 µs is the ITU R1 / Europe
  // standard; 75 µs is the FCC standard for the Americas and Japan.
  // Setting the wrong region makes the high-end either harsh or muddy.
  let wfmDeemphUs = $state<50 | 75>(50);

  // NFM / AM squelch threshold. The threshold is the post-channel-filter
  // envelope magnitude in dBFS; -120 disables the gate. The UI slider
  // exposes the useful range (-80..-10) plus a leftmost "off" stop.
  let squelchEnabled = $state(false);
  let squelchDb = $state(-40);
  let squelchOpen = $state(true);

  // Audio AGC: post-demod loudness normalisation. Off by default so the
  // user gets the honest signal; can be toggled live without restart.
  let agcOn = $state(false);

  let hrfInfo = $state<{
    boardName: string;
    firmwareVersion: string;
    partId: string;
    serialNumber: string;
  } | null>(null);
  let hrfInfoError = $state<string | null>(null);
  let hrfInfoBusy = $state(false);

  let sweepOpen = $state(false);
  /** Save the dial before sweep so we restore on exit. */
  let savedTuneBeforeSweep: number | null = null;

  function activeSource(): RtlSdrSource | HackRfSource | RtlTcpSource {
    if (inputMode === 'hackrf') return hackrfSource;
    if (inputMode === 'network') return netSource;
    return usbSource;
  }

  // Keep the sample-rate options in sync with the selected source. If the
  // user flips RTL ↔ HackRF and the current rate isn't valid for the new
  // source, snap to that source's default (2.4 MS/s). Reading sampleRate
  // via the local helper coerces any DOM-stringified value back to a
  // number so the .some() comparison is type-safe.
  $effect(() => {
    const list = inputMode === 'hackrf' ? HACKRF_SAMPLE_RATES : RTL_SAMPLE_RATES;
    availableSampleRates = list;
    const current = asValidRate(sampleRate);
    if (!list.some((r) => r.value === current)) {
      sampleRate = 2_400_000;
    } else if (current !== sampleRate) {
      // Force the type to number so downstream code sees a number.
      sampleRate = current;
    }
  });

  const audio = new AudioPipeline();
  let volume = $state(0.6);

  let rtlStatus = $state<RtlSdrStatus>('idle');
  let rtlError = $state<string | null>(null);
  let bytesWritten = $state(0);
  let bytesDropped = $state(0);
  let streamStartMs = $state<number | null>(null);
  let elapsedMs = $state(0);
  let fftFramesRendered = $state(0);

  let dbMin = $state(-80);
  let dbMax = $state(-20);
  let colormap = $state<ColormapName>('viridis');

  let latestBins = $state<Float32Array | null>(null);
  let rafHandle = 0;
  let unsubStats: (() => void) | null = null;
  let unsubFft: (() => void) | null = null;
  let unsubAudio: (() => void) | null = null;

  let audioSamplesPlayed = $state(0);
  let audioUnderrun = $state(0);
  let audioRingUsed = $state(0);
  let audioWorkletReady = $state(false);

  let helpOpen = $state(false);
  let freqEntryOpen = $state(false);
  let onboardingOpen = $state(false);

  let unsubRecAudio: (() => void) | null = null;
  let unsubCwText: (() => void) | null = null;
  let unsubRds: (() => void) | null = null;
  let unsubAdsb: (() => void) | null = null;
  let unsubSquelch: (() => void) | null = null;
  let unsubIqSamples: (() => void) | null = null;
  let cwDecodedText = $state('');
  let cwDecodedWpm = $state(0);

  let rdsSynced = $state(false);
  let rdsPi = $state(0);
  let rdsPs = $state('');
  let rdsRt = $state('');
  let rdsStereo = $state(false);

  // ---- Lifecycle ----

  onMount(async () => {
    // First-run onboarding: auto-open the modal unless the user dismissed
    // it on a prior visit.
    try {
      if (localStorage.getItem('moshon.onboarding.dismissed.v1') !== '1') {
        onboardingOpen = true;
      }
      const savedBridgeUrl = localStorage.getItem('moshon.bridgeUrl.v1');
      if (savedBridgeUrl) bridgeUrl = savedBridgeUrl;
      const savedTarget = localStorage.getItem('moshon.rtltcpTarget.v1');
      if (savedTarget !== null) rtlTcpTarget = savedTarget;
      const savedMode = localStorage.getItem('moshon.inputMode.v1');
      if (savedMode === 'usb' || savedMode === 'hackrf' || savedMode === 'network') {
        inputMode = savedMode;
      }
      const savedHrf = localStorage.getItem('moshon.hackrfGain.v1');
      if (savedHrf) {
        const parsed = JSON.parse(savedHrf) as Partial<typeof HACKRF_DEFAULT_GAIN>;
        if (typeof parsed.ampOn === 'boolean') hrfAmpOn = parsed.ampOn;
        if (typeof parsed.lnaDb === 'number') hrfLnaDb = parsed.lnaDb;
        if (typeof parsed.vgaDb === 'number') hrfVgaDb = parsed.vgaDb;
      }
      const savedAdv = localStorage.getItem('moshon.advanced.v1');
      if (savedAdv) {
        const a = JSON.parse(savedAdv) as Record<string, unknown>;
        if (typeof a.offsetEnabled === 'boolean') offsetEnabled = a.offsetEnabled;
        if (typeof a.offsetHz === 'number') offsetHz = a.offsetHz;
        if (typeof a.ppmCorrection === 'number') ppmCorrection = a.ppmCorrection;
        if (typeof a.rtlBiasT === 'boolean') rtlBiasT = a.rtlBiasT;
        if (a.rtlDirectSampling === 'off' || a.rtlDirectSampling === 'i' || a.rtlDirectSampling === 'q') {
          rtlDirectSampling = a.rtlDirectSampling;
        }
        if (typeof a.hrfAntennaPower === 'boolean') hrfAntennaPower = a.hrfAntennaPower;
      }
      const savedDeemph = localStorage.getItem('moshon.wfmDeemphUs.v1');
      if (savedDeemph === '50' || savedDeemph === '75') {
        wfmDeemphUs = Number(savedDeemph) as 50 | 75;
      }
      const savedSquelch = localStorage.getItem('moshon.squelch.v1');
      if (savedSquelch) {
        try {
          const s = JSON.parse(savedSquelch) as { enabled?: boolean; db?: number };
          if (typeof s.enabled === 'boolean') squelchEnabled = s.enabled;
          if (typeof s.db === 'number' && s.db >= -100 && s.db <= 0) squelchDb = s.db;
        } catch {
          // ignore malformed JSON
        }
      }
      const savedAgc = localStorage.getItem('moshon.agc.v1');
      if (savedAgc === '1') agcOn = true;
    } catch {
      // localStorage unavailable — skip onboarding entirely rather than
      // forcing it on every load.
    }

    // Restore tuning state from URL hash before anything else touches it.
    // Order matters: mode setter overwrites bandwidth and stepSize, so
    // apply mode first, then bandwidth, then frequency / gain.
    const hashState = readHash();
    if (hashState.mode) tuning.mode = hashState.mode;
    if (hashState.bandwidth) tuning.bandwidth = hashState.bandwidth;
    if (hashState.centerFreq) tuning.centerFreq = hashState.centerFreq;
    if (hashState.gain !== undefined) tuning.gain = hashState.gain;

    try {
      await init();
      smokeResult = smoke();
      wasmStatus = 'ready';
    } catch (err) {
      wasmStatus = 'error';
      wasmError = err instanceof Error ? err.message : String(err);
    }
  });

  // Mirror tuning into the URL hash so the page is shareable. Debounced via
  // history.replaceState so dragging the dial doesn't trash the back stack.
  $effect(() => {
    writeHash({
      centerFreq: tuning.centerFreq,
      mode: tuning.mode,
      bandwidth: tuning.bandwidth,
      gain: tuning.gain,
    });
  });

  // Persist bridge connection inputs. Bridge URL/target intentionally live in
  // localStorage and NOT the URL hash — see the no-bridge-in-hash decision
  // in MEMORY.md.
  $effect(() => {
    const url = bridgeUrl;
    const tgt = rtlTcpTarget;
    const mode = inputMode;
    try {
      localStorage.setItem('moshon.bridgeUrl.v1', url);
      localStorage.setItem('moshon.rtltcpTarget.v1', tgt);
      localStorage.setItem('moshon.inputMode.v1', mode);
    } catch {
      // ignore
    }
  });

  // Persist advanced settings + apply live to whichever source is active.
  $effect(() => {
    const oe = offsetEnabled;
    const oh = offsetHz;
    const ppm = ppmCorrection;
    const bt = rtlBiasT;
    const ds = rtlDirectSampling;
    const ap = hrfAntennaPower;
    try {
      localStorage.setItem(
        'moshon.advanced.v1',
        JSON.stringify({
          offsetEnabled: oe,
          offsetHz: oh,
          ppmCorrection: ppm,
          rtlBiasT: bt,
          rtlDirectSampling: ds,
          hrfAntennaPower: ap,
        }),
      );
    } catch {
      // ignore
    }
    // Live-apply when streaming. The offset retune path is identical
    // across all three sources.
    if (rtlStatus === 'streaming') {
      const off = oe ? oh : 0;
      activeSource().retune({ offsetHz: off });
      if (inputMode === 'usb') {
        usbSource.setAdvanced({
          ppmCorrection: ppm,
          biasT: bt,
          directSampling: ds,
        });
      } else if (inputMode === 'hackrf') {
        hackrfSource.setAdvanced({ antennaPower: ap });
      }
    }
  });

  // Persist + push WFM de-emphasis. Applies live to whichever source is
  // currently streaming; the source forwards the setDeemphasis message to
  // the DSP worker, which applies it to the running WfmDemod (no audio
  // glitch — the IIR state is preserved across the coefficient swap).
  $effect(() => {
    const us = wfmDeemphUs;
    try {
      localStorage.setItem('moshon.wfmDeemphUs.v1', String(us));
    } catch {
      // ignore
    }
    if (rtlStatus === 'streaming') {
      activeSource().setDeemphasis(us);
    }
  });

  // Persist + push squelch settings. -120 dBFS sentinel disables the gate
  // in the demod; the UI slider sits between -80 and -10.
  $effect(() => {
    const enabled = squelchEnabled;
    const db = squelchDb;
    try {
      localStorage.setItem('moshon.squelch.v1', JSON.stringify({ enabled, db }));
    } catch {
      // ignore
    }
    if (rtlStatus === 'streaming') {
      activeSource().setSquelch(enabled ? db : -120);
    }
  });

  // Persist + push audio AGC.
  $effect(() => {
    const on = agcOn;
    try {
      localStorage.setItem('moshon.agc.v1', on ? '1' : '0');
    } catch {
      // ignore
    }
    if (rtlStatus === 'streaming') {
      activeSource().setAgc(on);
    }
  });

  // Persist HackRF per-stage gain + push live changes to the worker.
  $effect(() => {
    const amp = hrfAmpOn;
    const lna = hrfLnaDb;
    const vga = hrfVgaDb;
    try {
      localStorage.setItem(
        'moshon.hackrfGain.v1',
        JSON.stringify({ ampOn: amp, lnaDb: lna, vgaDb: vga }),
      );
    } catch {
      // ignore
    }
    if (inputMode === 'hackrf' && rtlStatus === 'streaming') {
      hackrfSource.setHackrfGain({ ampOn: amp, lnaDb: lna, vgaDb: vga });
    }
  });

  onDestroy(() => {
    cancelAnimationFrame(rafHandle);
    unsubStats?.();
    unsubFft?.();
    void usbSource.disconnect();
    void hackrfSource.disconnect();
    void netSource.disconnect();
    void audio.close();
  });

  // Push volume / mute into the audio worklet whenever they change.
  // IMPORTANT: read the reactive state UNCONDITIONALLY before the ready
  // gate, otherwise Svelte 5 short-circuits and never tracks the dep — the
  // effect runs once at mount (when audio isn't ready), reads nothing
  // reactive, and is then dead. Capture into a local first.
  $effect(() => {
    const v = volume;
    if (audio.isReady) audio.setVolume(v);
  });
  $effect(() => {
    const m = tuning.muted;
    if (audio.isReady) audio.setMuted(m);
  });

  // ---- Retune effects ----
  // When centerFreq or gain change while streaming, push them to whichever
  // source is currently active without restarting the stream.
  $effect(() => {
    const f = tuning.centerFreq;
    if (rtlStatus === 'streaming') activeSource().retune({ centerFreq: f });
  });
  $effect(() => {
    const g = tuning.gain;
    if (rtlStatus === 'streaming') activeSource().retune({ gain: g });
  });

  // Push mode + bandwidth changes to the DSP worker without restarting.
  // Capture both reactive reads BEFORE the streaming gate so Svelte tracks
  // them as dependencies even on the first (non-streaming) tick.
  $effect(() => {
    const m = tuning.mode;
    const bw = tuning.bandwidth;
    if (rtlStatus === 'streaming') activeSource().setMode(m, bw);
  });

  // ---- Render loop (now just for elapsed timer) ----

  function tick() {
    if (streamStartMs !== null) {
      elapsedMs = performance.now() - streamStartMs;
    }
    rafHandle = requestAnimationFrame(tick);
  }

  // ---- Button actions ----

  function resetStreamCounters() {
    rtlError = null;
    bytesWritten = 0;
    bytesDropped = 0;
    fftFramesRendered = 0;
    streamStartMs = performance.now();
    elapsedMs = 0;
  }

  function wireStreamListeners() {
    const src = activeSource();
    unsubStats?.();
    unsubFft?.();
    unsubRecAudio?.();
    unsubStats = src.onStats((s) => {
      bytesWritten = s.bytesWritten;
      bytesDropped = s.bytesDropped;
    });
    unsubFft = src.onFft((evt) => {
      latestBins = evt.bins;
      fftFramesRendered++;
    });
    unsubRecAudio = src.onAudio((evt) => {
      const fits = recorder.push(evt.samples);
      if (!fits) {
        // Hit the memory cap — finalize the recording for the user.
        onRecordToggle();
      }
    });
    unsubCwText = src.onCwText((evt) => {
      // Cap the visible window so the buffer doesn't grow unbounded on
      // long sessions. Keep the most recent ~1500 chars.
      cwDecodedText = (cwDecodedText + evt.text).slice(-1500);
      cwDecodedWpm = evt.wpm;
    });
    unsubRds = src.onRds((evt) => {
      rdsSynced = evt.synced;
      rdsPi = evt.pi;
      rdsPs = evt.ps;
      rdsRt = evt.rt;
      rdsStereo = evt.stereo;
    });
    unsubAdsb = src.onAdsbFrames((evt) => {
      try {
        const frames = JSON.parse(evt.framesJson) as AdsbRawFrame[];
        for (const f of frames) aircraftTracker.ingest(f);
      } catch {
        // Bad JSON from the worker — drop the batch.
      }
    });
    unsubSquelch = src.onSquelch((evt) => {
      squelchOpen = evt.open;
    });
    unsubIqSamples = src.onIqSamples((evt) => {
      const fits = iqRecorder.push(evt.samples);
      if (!fits) {
        // Cap reached — finalize so the user doesn't lose what was captured.
        onIqRecordToggle();
      }
    });
  }

  async function startAudio() {
    // AudioContext can only be created from a user gesture, so do it here
    // (inside the Start click handler). Idempotent if already up.
    await audio.init();
    audio.setVolume(volume);
    audio.setMuted(tuning.muted);

    unsubAudio?.();
    unsubAudio = audio.onStats((s) => {
      audioSamplesPlayed = s.samplesPlayed;
      audioUnderrun = s.samplesUnderrun;
      audioRingUsed = s.ringUsedBytes;
      audioWorkletReady = audio.isWorkletReady;
    });
  }

  function clearStreamCleanup() {
    cancelAnimationFrame(rafHandle);
    unsubStats?.();
    unsubFft?.();
    unsubAudio?.();
    unsubRecAudio?.();
    unsubCwText?.();
    unsubRds?.();
    unsubAdsb?.();
    unsubSquelch?.();
    unsubIqSamples?.();
    unsubStats = null;
    unsubFft = null;
    unsubAudio = null;
    unsubRecAudio = null;
    unsubCwText = null;
    unsubRds = null;
    unsubAdsb = null;
    unsubSquelch = null;
    unsubIqSamples = null;
    // If recordings were in progress, save what we have.
    if (recorder.recording) recorder.stopAndDownload();
    if (iqRecorder.recording) {
      activeSource().setIqRecording(false);
      iqRecorder.stopAndDownload();
    }
  }

  // Wipe the decoded buffer whenever the user leaves CW mode so they don't
  // see stale text next time they tune back.
  $effect(() => {
    if (tuning.mode !== 'cw') {
      cwDecodedText = '';
      cwDecodedWpm = 0;
    }
  });

  // When entering ADS-B mode, auto-jump to 1090 MHz — that's the only
  // frequency Mode S squitters live on. Leaving the mode resets nothing
  // (the user might want the same dial back).
  $effect(() => {
    if (tuning.mode === 'adsb' && tuning.centerFreq !== 1_090_000_000) {
      tuning.centerFreq = 1_090_000_000;
    }
    // Clear the aircraft list when leaving ADS-B so the panel starts fresh
    // on the next entry. Stale entries hang around briefly if we don't.
    if (tuning.mode !== 'adsb') {
      aircraftTracker.clear();
    }
  });

  // LoRa mode auto-tunes to EU868 ch0 (868.1 MHz). Other regions and
  // channels can be reached by manual tuning — `,`/`.` step by 200 kHz
  // which lands cleanly on ch1/ch2.
  $effect(() => {
    if (tuning.mode === 'lora' && tuning.centerFreq !== 868_100_000) {
      tuning.centerFreq = 868_100_000;
    }
  });

  // Periodic stale-aircraft evict so the list doesn't grow unbounded.
  let evictTimer = 0;
  $effect(() => {
    if (rtlStatus === 'streaming' && tuning.mode === 'adsb') {
      evictTimer = window.setInterval(() => aircraftTracker.evictStale(), 5_000);
      return () => window.clearInterval(evictTimer);
    }
  });

  function onRecordToggle() {
    if (recorder.recording) {
      activeSource().setRecording(false);
      recorder.stopAndDownload();
    } else {
      recorder.start();
      activeSource().setRecording(true);
    }
  }

  function onIqRecordToggle() {
    if (iqRecorder.recording) {
      activeSource().setIqRecording(false);
      iqRecorder.stopAndDownload();
    } else {
      iqRecorder.start({
        sampleRate: asValidRate(sampleRate),
        centerFreqHz: tuning.centerFreq,
      });
      activeSource().setIqRecording(true);
    }
  }

  async function onConnect() {
    // Local-USB path (RTL-SDR or HackRF). The WebUSB picker needs a user
    // gesture, so each click reopens whichever device matches the
    // current inputMode.
    rtlError = null;
    rtlStatus = 'connecting';
    try {
      if (inputMode === 'hackrf') {
        await hackrfSource.connect();
      } else {
        await usbSource.connect();
      }
      rtlStatus = 'connected';
    } catch (err) {
      rtlStatus = 'error';
      rtlError = err instanceof Error ? err.message : String(err);
    }
  }

  async function onStart() {
    resetStreamCounters();
    wireStreamListeners();
    rafHandle = requestAnimationFrame(tick);

    try {
      await startAudio();
      rtlStatus = 'streaming';
      const baseOpts = {
        sampleRate: asValidRate(sampleRate),
        centerFreq: tuning.centerFreq,
        gain: tuning.gain,
        fftSize: FFT_SIZE,
        fftRateHz: FFT_RATE_HZ,
        mode: tuning.mode,
        bandwidthHz: tuning.bandwidth,
        offsetHz: offsetEnabled ? offsetHz : 0,
        deemphasisUs: wfmDeemphUs,
        squelchDb: squelchEnabled ? squelchDb : -120,
        agcOn,
        audioRing: audio.ring!.buffer,
      };
      if (inputMode === 'hackrf') {
        await hackrfSource.start({
          ...baseOpts,
          hackrfGain: { ampOn: hrfAmpOn, lnaDb: hrfLnaDb, vgaDb: hrfVgaDb },
          antennaPower: hrfAntennaPower,
        });
      } else {
        await usbSource.start({
          ...baseOpts,
          ppmCorrection,
          biasT: rtlBiasT,
          directSampling: rtlDirectSampling,
        });
      }
    } catch (err) {
      rtlStatus = 'error';
      rtlError = err instanceof Error ? err.message : String(err);
      clearStreamCleanup();
    }
  }

  async function onConnectNetwork() {
    resetStreamCounters();
    wireStreamListeners();
    rafHandle = requestAnimationFrame(tick);
    rtlStatus = 'connecting';

    try {
      await startAudio();
      await netSource.start({
        bridgeUrl,
        rtlTcpTarget: rtlTcpTarget.trim() || undefined,
        sampleRate: asValidRate(sampleRate),
        centerFreq: tuning.centerFreq,
        gain: tuning.gain,
        fftSize: FFT_SIZE,
        fftRateHz: FFT_RATE_HZ,
        mode: tuning.mode,
        bandwidthHz: tuning.bandwidth,
        offsetHz: offsetEnabled ? offsetHz : 0,
        deemphasisUs: wfmDeemphUs,
        squelchDb: squelchEnabled ? squelchDb : -120,
        agcOn,
        audioRing: audio.ring!.buffer,
      });
      rtlStatus = 'streaming';
    } catch (err) {
      rtlStatus = 'error';
      rtlError = err instanceof Error ? err.message : String(err);
      clearStreamCleanup();
    }
  }

  async function onStop() {
    cancelAnimationFrame(rafHandle);
    rtlStatus = 'closing';
    try {
      const src = activeSource();
      await src.stop();
      await src.disconnect();
      rtlStatus = 'idle';
    } catch (err) {
      rtlStatus = 'error';
      rtlError = err instanceof Error ? err.message : String(err);
    } finally {
      clearStreamCleanup();
    }
  }

  async function onDisconnect() {
    cancelAnimationFrame(rafHandle);
    unsubStats?.();
    unsubFft?.();
    unsubStats = null;
    unsubFft = null;
    rtlStatus = 'closing';
    try {
      await activeSource().disconnect();
      rtlStatus = 'idle';
      bytesWritten = 0;
      bytesDropped = 0;
      fftFramesRendered = 0;
      streamStartMs = null;
      elapsedMs = 0;
    } catch (err) {
      rtlStatus = 'error';
      rtlError = err instanceof Error ? err.message : String(err);
    }
  }

  async function onFetchHrfInfo() {
    if (inputMode !== 'hackrf') return;
    hrfInfoBusy = true;
    hrfInfoError = null;
    try {
      hrfInfo = await hackrfSource.getDeviceInfo();
    } catch (err) {
      hrfInfoError = err instanceof Error ? err.message : String(err);
    } finally {
      hrfInfoBusy = false;
    }
  }

  function openSweep() {
    if (rtlStatus !== 'streaming') return;
    savedTuneBeforeSweep = tuning.centerFreq;
    sweepOpen = true;
  }

  function closeSweep() {
    sweepOpen = false;
    if (savedTuneBeforeSweep !== null) {
      tuning.centerFreq = savedTuneBeforeSweep;
      savedTuneBeforeSweep = null;
    }
  }

  function onSweepRetune(hz: number) {
    tuning.centerFreq = hz;
  }

  function onSweepPick(hz: number) {
    sweepOpen = false;
    tuning.centerFreq = hz;
    savedTuneBeforeSweep = null;
  }

  function onClickToTune(hz: number) {
    tuning.centerFreq = hz;
  }

  // ---- Keyboard hotkeys ----
  function onWindowKey(e: KeyboardEvent) {
    // Don't hijack typing in inputs / textareas / contenteditable.
    const target = e.target as HTMLElement | null;
    if (
      target &&
      (target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable)
    ) {
      return;
    }
    if (e.altKey || e.ctrlKey || e.metaKey) return;

    switch (e.key) {
      case 'f':
      case 'F':
        e.preventDefault();
        freqEntryOpen = true;
        break;
      case 'm':
      case 'M':
        e.preventDefault();
        tuning.cycleMode();
        break;
      case 'b':
      case 'B':
        e.preventDefault();
        tuning.cycleBandwidth();
        break;
      case 'g':
      case 'G':
        e.preventDefault();
        tuning.cycleGain();
        break;
      case ',':
      case '<':
        e.preventDefault();
        tuning.stepDown();
        break;
      case '.':
      case '>':
        e.preventDefault();
        tuning.stepUp();
        break;
      case '[':
      case '{':
        e.preventDefault();
        tuning.cycleStepSize(-1);
        break;
      case ']':
      case '}':
        e.preventDefault();
        tuning.cycleStepSize(1);
        break;
      case ' ':
        e.preventDefault();
        tuning.toggleMute();
        break;
      case '?':
        e.preventDefault();
        helpOpen = true;
        break;
    }
  }

  // ---- Derived display values ----

  const BYTES_PER_SAMPLE = 2;
  let samplesTotal = $derived(bytesWritten / BYTES_PER_SAMPLE);
  let rateMSps = $derived(
    elapsedMs > 0 ? samplesTotal / 1e6 / (elapsedMs / 1000) : 0,
  );
  let renderFps = $derived(
    elapsedMs > 0 ? fftFramesRendered / (elapsedMs / 1000) : 0,
  );

  // ---- S-meter (B7) ----
  // Peak dBFS across the bins covering the channel bandwidth. Refreshes
  // on every FFT frame via the latestBins reactive dep.
  let signalDb = $derived(
    latestBins
      ? peakDbInChannel(latestBins, sampleRate, tuning.bandwidth)
      : Number.NEGATIVE_INFINITY,
  );
  let signalS = $derived(dbToSUnit(signalDb));

  function formatMSamples(n: number): string {
    if (n >= 1e6) return `${(n / 1e6).toFixed(2)} MS`;
    if (n >= 1e3) return `${(n / 1e3).toFixed(1)} kS`;
    return `${n} S`;
  }

  function gainLabel(g: number | null): string {
    return g === null ? 'AGC' : `${g} dB`;
  }
</script>

<svelte:window onkeydown={onWindowKey} />

<HotkeyHelp bind:open={helpOpen} />
<FrequencyEntry
  bind:open={freqEntryOpen}
  initialValue={(tuning.centerFreq / 1e6).toString()}
  onSubmit={(hz) => (tuning.centerFreq = hz)}
/>
<Onboarding bind:open={onboardingOpen} />

<div class="h-screen w-screen overflow-hidden flex flex-col bg-neutral-950 text-neutral-200">
  <!-- Compact top bar — always visible. Brand + DSP/stream status +
       help / setup. Logout will live here once auth lands. -->
  <header
    class="flex items-center gap-3 px-3 py-2 border-b border-neutral-800 bg-neutral-950 shrink-0"
  >
    <Radio size={18} class="text-(--color-accent)" strokeWidth={1.75} />
    <h1 class="text-sm font-medium tracking-tight">Moshon SDR</h1>

    <!-- DSP smoke as a discreet status pill -->
    <span
      class="px-2 py-0.5 rounded text-[10px] font-mono border"
      class:border-emerald-700={wasmStatus === 'ready'}
      class:text-emerald-400={wasmStatus === 'ready'}
      class:border-amber-700={wasmStatus === 'error'}
      class:text-amber-400={wasmStatus === 'error'}
      class:border-neutral-800={wasmStatus === 'pending'}
      class:text-neutral-500={wasmStatus === 'pending'}
      title={wasmStatus === 'ready'
        ? `DSP smoke test: ${smokeResult}`
        : wasmStatus === 'error'
          ? wasmError ?? 'DSP failed'
          : 'Loading DSP module…'}
    >
      {wasmStatus === 'ready'
        ? `DSP ${smokeResult}`
        : wasmStatus === 'pending'
          ? 'DSP…'
          : 'DSP fail'}
    </span>

    {#if rtlStatus !== 'idle'}
      <span
        class="px-2 py-0.5 rounded text-[10px] font-mono border"
        class:bg-emerald-950={rtlStatus === 'streaming'}
        class:border-emerald-700={rtlStatus === 'streaming'}
        class:text-emerald-400={rtlStatus === 'streaming'}
        class:border-amber-700={rtlStatus === 'connecting' ||
          rtlStatus === 'closing' ||
          rtlStatus === 'connected'}
        class:text-amber-300={rtlStatus === 'connecting' ||
          rtlStatus === 'closing' ||
          rtlStatus === 'connected'}
        class:border-rose-700={rtlStatus === 'error'}
        class:text-rose-400={rtlStatus === 'error'}
      >
        {rtlStatus}
      </span>
    {/if}

    {#if rtlStatus === 'streaming'}
      <span class="text-[10px] font-mono text-neutral-500 hidden md:inline">
        {rateMSps.toFixed(2)} MS/s · {renderFps.toFixed(0)} fps
      </span>
    {/if}

    <div class="flex-1"></div>

    <button
      type="button"
      onclick={() => (onboardingOpen = true)}
      class="inline-flex items-center gap-1 rounded border border-neutral-700 text-neutral-400 hover:text-neutral-200 px-2 py-1 text-[11px] font-mono cursor-pointer"
      title="WebUSB setup help"
    >
      <HelpCircle size={12} />
      <span class="hidden sm:inline">Setup</span>
    </button>
    <button
      type="button"
      onclick={() => (helpOpen = true)}
      class="inline-flex items-center gap-1 rounded border border-neutral-700 text-neutral-400 hover:text-neutral-200 px-2 py-1 text-[11px] font-mono cursor-pointer"
      title="Keyboard shortcuts (?)"
    >
      <Keyboard size={12} />
      <span class="hidden sm:inline">?</span>
    </button>
  </header>

  <!-- Body: fixed-width left sidebar + flexible main viewport -->
  <div class="flex flex-1 overflow-hidden">
    <aside
      class="w-full md:w-[360px] shrink-0 border-r border-neutral-800 overflow-y-auto bg-neutral-950/60"
      aria-label="Controls"
    >
      <!-- Controls live in the existing inline blocks below. They'll
           get wrapped in <Section> primitives in phase 1b; for now the
           original <section> markup is preserved verbatim inside the
           sidebar so the rewrite ships in safe increments. -->
      <section class="p-3 sm:p-4">
    <!-- Inner header removed — Setup / Help / status moved to the top bar. -->
    <h2 class="text-[11px] font-mono uppercase tracking-wide text-neutral-500 mb-3">
      {inputMode === 'hackrf' ? 'HackRF' : inputMode === 'network' ? 'Network' : 'RTL-SDR'} controls
    </h2>

    {#if rtlStatus === 'idle'}
      <!-- Input mode picker. RTL-SDR and HackRF are both WebUSB; the
           network tab proxies a remote rtl_tcp via the moshon-bridge
           daemon. -->
      <div class="flex gap-1 mb-4 text-xs font-mono flex-wrap">
        <button
          type="button"
          onclick={() => (inputMode = 'usb')}
          class="inline-flex items-center gap-1.5 rounded px-3 py-1.5 cursor-pointer"
          class:bg-neutral-800={inputMode === 'usb'}
          class:border-neutral-600={inputMode === 'usb'}
          class:text-neutral-100={inputMode === 'usb'}
          class:bg-neutral-950={inputMode !== 'usb'}
          class:border-neutral-800={inputMode !== 'usb'}
          class:text-neutral-400={inputMode !== 'usb'}
          style="border-width: 1px; border-style: solid;"
          aria-pressed={inputMode === 'usb'}
        >
          <Usb size={12} />
          RTL-SDR
        </button>
        <button
          type="button"
          onclick={() => (inputMode = 'hackrf')}
          class="inline-flex items-center gap-1.5 rounded px-3 py-1.5 cursor-pointer"
          class:bg-neutral-800={inputMode === 'hackrf'}
          class:border-neutral-600={inputMode === 'hackrf'}
          class:text-neutral-100={inputMode === 'hackrf'}
          class:bg-neutral-950={inputMode !== 'hackrf'}
          class:border-neutral-800={inputMode !== 'hackrf'}
          class:text-neutral-400={inputMode !== 'hackrf'}
          style="border-width: 1px; border-style: solid;"
          aria-pressed={inputMode === 'hackrf'}
        >
          <Usb size={12} />
          HackRF
        </button>
        <button
          type="button"
          onclick={() => (inputMode = 'network')}
          class="inline-flex items-center gap-1.5 rounded px-3 py-1.5 cursor-pointer"
          class:bg-neutral-800={inputMode === 'network'}
          class:border-neutral-600={inputMode === 'network'}
          class:text-neutral-100={inputMode === 'network'}
          class:bg-neutral-950={inputMode !== 'network'}
          class:border-neutral-800={inputMode !== 'network'}
          class:text-neutral-400={inputMode !== 'network'}
          style="border-width: 1px; border-style: solid;"
          aria-pressed={inputMode === 'network'}
        >
          <Wifi size={12} />
          Network (rtl_tcp)
        </button>
      </div>

      <!-- Sample-rate picker. Held for the session — switching requires
           reconnect because every demod's decimation factor changes with
           the input rate. -->
      <label class="flex items-center gap-3 mb-4 text-xs font-mono">
        <span class="text-neutral-500 uppercase text-[10px]">Rate</span>
        <select
          bind:value={sampleRate}
          class="rounded border border-neutral-700 bg-neutral-950 px-2 py-1 text-neutral-200"
        >
          {#each availableSampleRates as r (r.value)}
            <option value={r.value}>{r.label}</option>
          {/each}
        </select>
        <span class="text-neutral-600 text-[10px]">
          higher = wider spectrum, more CPU. Switching requires reconnect.
        </span>
      </label>

      {#if inputMode === 'usb'}
        <p class="text-sm text-neutral-400 mb-4">
          Plug in an RTL-SDR Blog v3/v4 dongle and click Connect.
        </p>
        <button
          type="button"
          onclick={onConnect}
          class="inline-flex items-center gap-2 rounded-md bg-(--color-accent) text-neutral-950 px-4 py-2 text-sm font-medium hover:bg-(--color-accent-strong) cursor-pointer"
        >
          <Plug size={16} />
          Connect RTL-SDR
        </button>
      {:else if inputMode === 'hackrf'}
        <p class="text-sm text-neutral-400 mb-4">
          Plug in a HackRF One (or compatible). 1 MHz – 6 GHz tuning range,
          wider passband than RTL-SDR. The first connection takes a moment
          while we configure the device's filter and PLL.
        </p>
        <button
          type="button"
          onclick={onConnect}
          class="inline-flex items-center gap-2 rounded-md bg-(--color-accent) text-neutral-950 px-4 py-2 text-sm font-medium hover:bg-(--color-accent-strong) cursor-pointer"
        >
          <Plug size={16} />
          Connect HackRF
        </button>
      {:else}
        <NetworkConnect
          bind:bridgeUrl
          bind:rtlTcpTarget
          onConnect={onConnectNetwork}
        />
      {/if}
    {:else if rtlStatus === 'connecting'}
      <div class="flex items-center gap-2 text-sm text-neutral-300">
        <Loader2 size={16} class="animate-spin" />
        Waiting for device picker…
      </div>
    {:else if rtlStatus === 'connected' || rtlStatus === 'streaming' || rtlStatus === 'closing'}
      <!-- Spectrum/VFO/decode panels live in the main area on the right.
           This sidebar branch only carries the controls (audio row,
           gain, advanced, memory, start/stop) below. -->

      <!-- Audio: volume slider + mute + record. Wraps to a second row on
           narrow viewports so the slider always gets a sensible width. -->
      <div class="flex items-center flex-wrap gap-2 sm:gap-3 mb-4 text-xs font-mono">
        <button
          type="button"
          onclick={() => tuning.toggleMute()}
          class="inline-flex items-center gap-2 rounded border border-neutral-700 px-3 py-1.5 hover:border-neutral-500 cursor-pointer"
          class:bg-amber-950={tuning.muted}
          class:border-amber-700={tuning.muted}
          class:text-amber-300={tuning.muted}
          class:text-neutral-300={!tuning.muted}
          aria-pressed={tuning.muted}
          title="Mute (Space)"
        >
          {#if tuning.muted}
            <VolumeX size={14} />
            <span>Muted</span>
          {:else}
            <Volume2 size={14} />
            <span>Audio</span>
          {/if}
        </button>
        <!-- AGC: levels station-to-station loudness. Works for any
             demod. Off by default; toggle persists across reloads. -->
        <button
          type="button"
          onclick={() => (agcOn = !agcOn)}
          class="inline-flex items-center rounded border px-2 py-1 text-[10px] uppercase cursor-pointer"
          class:bg-(--color-accent)={agcOn}
          class:text-neutral-950={agcOn}
          class:border-(--color-accent)={agcOn}
          class:text-neutral-400={!agcOn}
          class:border-neutral-700={!agcOn}
          aria-pressed={agcOn}
          title="Auto level audio output"
        >AGC</button>

        <label class="order-3 sm:order-none basis-full sm:basis-auto flex-1 flex items-center gap-3 min-w-0">
          <span class="text-neutral-500 uppercase text-[10px]">Vol</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            bind:value={volume}
            class="flex-1"
          />
          <span class="text-neutral-300 w-10 text-right tabular-nums">
            {Math.round(volume * 100)}%
          </span>
        </label>

        <!-- Squelch (NFM/AM). Two-part control: enable toggle + threshold
             slider. Indicator dot turns green when the gate is open. -->
        {#if tuning.mode === 'nfm' || tuning.mode === 'am'}
          <div
            class="inline-flex items-center gap-2 rounded border border-neutral-700 px-2 py-1"
            aria-label="Squelch"
          >
            <button
              type="button"
              onclick={() => (squelchEnabled = !squelchEnabled)}
              class="text-[10px] uppercase cursor-pointer"
              class:text-(--color-accent)={squelchEnabled}
              class:text-neutral-500={!squelchEnabled}
              aria-pressed={squelchEnabled}
              title="Toggle squelch"
            >SQ</button>
            {#if squelchEnabled}
              <span
                class="inline-block w-1.5 h-1.5 rounded-full"
                class:bg-emerald-400={squelchOpen}
                class:bg-neutral-700={!squelchOpen}
                title={squelchOpen ? 'gate open' : 'gate closed'}
              ></span>
              <input
                type="range"
                min="-80"
                max="-10"
                step="1"
                bind:value={squelchDb}
                class="w-24"
                title="Squelch threshold (dBFS)"
              />
              <span class="text-[10px] text-neutral-400 tabular-nums w-10 text-right">
                {squelchDb}dB
              </span>
            {/if}
          </div>
        {/if}

        <!-- WFM de-emphasis (mode-conditional). Europe is 50 µs, Americas
             is 75 µs. Live-applied to the running demod — no glitch. -->
        {#if tuning.mode === 'wfm'}
          <div
            class="inline-flex items-center gap-1 rounded border border-neutral-700 px-2 py-1"
            title="WFM audio de-emphasis time constant"
            aria-label="WFM de-emphasis"
          >
            <span class="text-neutral-500 uppercase text-[10px] mr-1">De-emph</span>
            <button
              type="button"
              onclick={() => (wfmDeemphUs = 50)}
              class="px-1.5 rounded text-[10px] cursor-pointer"
              class:bg-(--color-accent)={wfmDeemphUs === 50}
              class:text-neutral-950={wfmDeemphUs === 50}
              class:text-neutral-400={wfmDeemphUs !== 50}
              aria-pressed={wfmDeemphUs === 50}
              title="ITU R1 / Europe"
            >50µs</button>
            <button
              type="button"
              onclick={() => (wfmDeemphUs = 75)}
              class="px-1.5 rounded text-[10px] cursor-pointer"
              class:bg-(--color-accent)={wfmDeemphUs === 75}
              class:text-neutral-950={wfmDeemphUs === 75}
              class:text-neutral-400={wfmDeemphUs !== 75}
              aria-pressed={wfmDeemphUs === 75}
              title="FCC / Americas / Japan"
            >75µs</button>
          </div>
        {/if}

        <!-- Recorder (M2.1) -->
        <button
          type="button"
          onclick={onRecordToggle}
          disabled={rtlStatus !== 'streaming'}
          class="inline-flex items-center gap-2 rounded border px-3 py-1.5 cursor-pointer
                 disabled:opacity-40 disabled:cursor-not-allowed"
          class:bg-rose-950={recorder.recording}
          class:border-rose-700={recorder.recording}
          class:text-rose-300={recorder.recording}
          class:border-neutral-700={!recorder.recording}
          class:text-neutral-300={!recorder.recording}
          aria-pressed={recorder.recording}
          title={recorder.recording
            ? `Stop & download (${recorder.seconds.toFixed(0)} s captured, cap ${recorder.capMinutes} min)`
            : 'Record demodulated audio to WAV'}
        >
          {#if recorder.recording}
            <Circle size={10} fill="currentColor" class="animate-pulse" />
            <span class="tabular-nums">{recorder.seconds.toFixed(0)}s</span>
          {:else}
            <Download size={14} />
            <span>Rec</span>
          {/if}
        </button>

        <!-- IQ recorder — captures raw IQ to SDR#-compatible WAV.
             At 2.4 MS/s the 256 MB cap holds ~53 s; HackRF rates burn
             through it much faster. -->
        <button
          type="button"
          onclick={onIqRecordToggle}
          disabled={rtlStatus !== 'streaming'}
          class="inline-flex items-center gap-2 rounded border px-3 py-1.5 cursor-pointer
                 disabled:opacity-40 disabled:cursor-not-allowed"
          class:bg-amber-950={iqRecorder.recording}
          class:border-amber-700={iqRecorder.recording}
          class:text-amber-300={iqRecorder.recording}
          class:border-neutral-700={!iqRecorder.recording}
          class:text-neutral-300={!iqRecorder.recording}
          aria-pressed={iqRecorder.recording}
          title={iqRecorder.recording
            ? `Stop & download IQ (${iqRecorder.seconds.toFixed(1)} s, ${(iqRecorder.totalBytes / 1024 / 1024).toFixed(0)} MB, cap ${iqRecorder.capMb} MB)`
            : 'Record raw IQ to SDR#-compatible WAV'}
        >
          {#if iqRecorder.recording}
            <Circle size={10} fill="currentColor" class="animate-pulse" />
            <span class="tabular-nums">{iqRecorder.seconds.toFixed(0)}s IQ</span>
          {:else}
            <Download size={14} />
            <span>Rec IQ</span>
          {/if}
        </button>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 text-xs font-mono">
        <label class="flex flex-col gap-1">
          <span class="text-neutral-500 uppercase">Colormap</span>
          <select
            bind:value={colormap}
            class="rounded border border-neutral-700 bg-neutral-900 px-2 py-1 text-neutral-200"
          >
            <option value="viridis">Viridis</option>
            <option value="magma">Magma</option>
            <option value="classic">Classic</option>
          </select>
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-neutral-500 uppercase">dB min ({dbMin})</span>
          <input
            type="range"
            min="-120"
            max="-40"
            step="1"
            bind:value={dbMin}
          />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-neutral-500 uppercase">dB max ({dbMax})</span>
          <input
            type="range"
            min="-60"
            max="0"
            step="1"
            bind:value={dbMax}
          />
        </label>
      </div>

      <!-- Advanced device settings (collapsible). Always available, but
           the source-specific rows hide for inputs that don't support
           them (RTL-SDR has bias-T / PPM / direct sampling; HackRF has
           antenna power; network rtl_tcp has only offset tuning). -->
      <details
        bind:open={advOpen}
        class="mb-4 rounded-md border border-neutral-800 bg-neutral-900 text-xs font-mono"
      >
        <summary class="cursor-pointer select-none px-3 py-2 text-neutral-400 uppercase hover:text-neutral-200">
          Advanced
        </summary>
        <div class="p-3 border-t border-neutral-800 space-y-3">
          <!-- Read-only sample-rate readout while streaming. Set before
               connect via the dropdown in the connect section above. -->
          <div class="text-[11px] text-neutral-500">
            Sample rate: <span class="text-neutral-300">{(sampleRate / 1e6).toFixed(2)} MS/s</span>
            <span class="text-neutral-600 ml-2">(locked while streaming)</span>
          </div>

          <!-- Spectrum sweep launcher. -->
          <div class="text-[11px]">
            <button
              type="button"
              onclick={openSweep}
              disabled={sweepOpen}
              class="rounded border border-neutral-700 px-2 py-1 text-neutral-300
                     hover:border-neutral-500 cursor-pointer disabled:opacity-50
                     disabled:cursor-not-allowed"
            >
              Open spectrum sweep…
            </button>
            <span class="text-neutral-600 ml-2">
              wide-band scan via repeated retunes; click peaks to tune
            </span>
          </div>

          <!-- Offset tuning (universal). -->
          <div>
            <label class="flex items-center gap-2 mb-1.5">
              <input type="checkbox" bind:checked={offsetEnabled} />
              <span class="text-neutral-300 uppercase">Offset tuning</span>
              <span class="text-neutral-600 normal-case">
                physical LO = dial + offset; NCO shifts back. DC spike moves off-channel.
              </span>
            </label>
            <label class="flex items-center gap-3 ml-6" class:opacity-50={!offsetEnabled}>
              <span class="text-neutral-500 uppercase text-[10px] w-12">Offset</span>
              <input
                type="range"
                min="50000"
                max="500000"
                step="10000"
                bind:value={offsetHz}
                disabled={!offsetEnabled}
                class="flex-1"
              />
              <span class="text-(--color-accent) w-16 text-right tabular-nums">
                {(offsetHz / 1000).toFixed(0)} kHz
              </span>
            </label>
          </div>

          {#if inputMode === 'usb'}
            <div class="border-t border-neutral-800 pt-3 space-y-2">
              <div class="text-neutral-500 uppercase mb-1">RTL-SDR</div>

              <label class="flex items-center gap-3">
                <span class="text-neutral-500 uppercase text-[10px] w-12">PPM</span>
                <input
                  type="range"
                  min="-200"
                  max="200"
                  step="1"
                  bind:value={ppmCorrection}
                  class="flex-1"
                />
                <span class="text-(--color-accent) w-16 text-right tabular-nums">
                  {ppmCorrection > 0 ? '+' : ''}{ppmCorrection}
                </span>
              </label>

              <label class="flex items-center gap-2">
                <input type="checkbox" bind:checked={rtlBiasT} />
                <span class="text-neutral-300">Bias-T</span>
                <span class="text-neutral-600 text-[10px]">
                  powers external LNAs via coax (~4.5 V/100 mA on RTL-SDR Blog)
                </span>
              </label>

              <label class="flex items-center gap-3">
                <span class="text-neutral-500 uppercase text-[10px] w-12">Direct</span>
                <select
                  bind:value={rtlDirectSampling}
                  class="rounded border border-neutral-700 bg-neutral-950 px-2 py-1 text-neutral-200"
                >
                  <option value="off">Off (normal RF path)</option>
                  <option value="i">I branch (HF below 24 MHz)</option>
                  <option value="q">Q branch (HF below 24 MHz)</option>
                </select>
                <span class="text-neutral-600 text-[10px]">
                  bypasses the tuner — pick I or Q based on board, leave Off otherwise
                </span>
              </label>
            </div>
          {/if}

          {#if inputMode === 'hackrf'}
            <div class="border-t border-neutral-800 pt-3 space-y-2">
              <div class="text-neutral-500 uppercase mb-1">HackRF</div>
              <label class="flex items-center gap-2">
                <input type="checkbox" bind:checked={hrfAntennaPower} />
                <span class="text-neutral-300">Antenna power</span>
                <span class="text-neutral-600 text-[10px]">
                  ~3.0 V/50 mA on the antenna port for active antennas / LNAs
                </span>
              </label>

              <div class="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  onclick={onFetchHrfInfo}
                  disabled={hrfInfoBusy}
                  class="rounded border border-neutral-700 px-2 py-1 text-neutral-300
                         hover:border-neutral-500 cursor-pointer disabled:opacity-50
                         disabled:cursor-not-allowed"
                >
                  {hrfInfoBusy ? 'Reading…' : 'Read device info'}
                </button>
                {#if hrfInfoError}
                  <span class="text-amber-400 text-[10px]">{hrfInfoError}</span>
                {/if}
              </div>
              {#if hrfInfo}
                <dl class="grid grid-cols-[6rem_1fr] gap-x-3 gap-y-0.5 text-[11px]">
                  <dt class="text-neutral-500">Board</dt>
                  <dd class="text-neutral-200">{hrfInfo.boardName}</dd>
                  <dt class="text-neutral-500">Firmware</dt>
                  <dd class="text-neutral-200 break-words">{hrfInfo.firmwareVersion || '—'}</dd>
                  <dt class="text-neutral-500">Part ID</dt>
                  <dd class="text-neutral-300 break-all">{hrfInfo.partId}</dd>
                  <dt class="text-neutral-500">Serial</dt>
                  <dd class="text-neutral-300 break-all">{hrfInfo.serialNumber}</dd>
                </dl>
              {/if}
            </div>
          {/if}

          {#if inputMode === 'usb'}
            <div class="border-t border-neutral-800 pt-3 space-y-1 text-[11px]">
              <div class="text-neutral-500 uppercase mb-1">RTL-SDR (USB descriptor)</div>
              <p class="text-neutral-400">
                Tuner type isn't reported by the WebUSB driver. Approximate
                identification: VID:PID and product string from the device
                descriptor. Most RTL-SDR Blog v3/v4 dongles use the R820T2 or
                R828D tuner.
              </p>
            </div>
          {/if}
        </div>
      </details>

      <!-- HackRF gain stages (M2.5b). Three independent controls per the
           official docs. Start values are RF=off, IF=16, BB=16; adjust
           IF and BB roughly together and only enable RF if signals are
           weak. Changes apply live to the device. -->
      {#if inputMode === 'hackrf'}
        <div
          class="mb-4 rounded-md border border-neutral-800 bg-neutral-900 p-3 font-mono text-xs"
          aria-label="HackRF gain stages"
        >
          <header class="flex items-center justify-between mb-2 text-neutral-500 uppercase">
            <span>HackRF gain</span>
            <span class="text-neutral-600 normal-case">
              total ≈ {(hrfAmpOn ? HACKRF_AMP_DB : 0) + hrfLnaDb + hrfVgaDb} dB
            </span>
          </header>

          <div class="flex items-center gap-3 mb-2">
            <button
              type="button"
              onclick={() => (hrfAmpOn = !hrfAmpOn)}
              class="rounded border px-2 py-1 cursor-pointer"
              class:bg-amber-950={hrfAmpOn}
              class:border-amber-700={hrfAmpOn}
              class:text-amber-300={hrfAmpOn}
              class:border-neutral-700={!hrfAmpOn}
              class:text-neutral-400={!hrfAmpOn}
              aria-pressed={hrfAmpOn}
              title="RF amplifier (~+11 dB). Use only for weak signals."
            >
              RF +{HACKRF_AMP_DB} dB · {hrfAmpOn ? 'ON' : 'off'}
            </button>
            <span class="text-neutral-600 text-[10px]">
              pre-amp before LNA — adds noise, use sparingly
            </span>
          </div>

          <label class="flex items-center gap-3 mb-1.5">
            <span class="text-neutral-500 uppercase text-[10px] w-8">IF</span>
            <input
              type="range"
              min="0"
              max="40"
              step="8"
              bind:value={hrfLnaDb}
              class="flex-1"
            />
            <span class="text-(--color-accent) w-12 text-right tabular-nums">
              {hrfLnaDb} dB
            </span>
          </label>

          <label class="flex items-center gap-3">
            <span class="text-neutral-500 uppercase text-[10px] w-8">BB</span>
            <input
              type="range"
              min="0"
              max="62"
              step="2"
              bind:value={hrfVgaDb}
              class="flex-1"
            />
            <span class="text-(--color-accent) w-12 text-right tabular-nums">
              {hrfVgaDb} dB
            </span>
          </label>
        </div>
      {/if}

      <!-- Memory channels (B7) -->
      <div class="mb-4">
        <MemoryChannels />
      </div>

      {#if rtlStatus === 'streaming' || (rtlStatus === 'closing' && bytesWritten > 0)}
        <dl
          class="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-2 rounded-md border border-neutral-800 bg-neutral-900 p-3 font-mono text-xs"
        >
          <div>
            <dt class="text-neutral-500">USB rate</dt>
            <dd class="text-(--color-accent) text-sm">{rateMSps.toFixed(2)} MS/s</dd>
          </div>
          <div>
            <dt class="text-neutral-500">Spectrum</dt>
            <dd class="text-neutral-200 text-sm">{renderFps.toFixed(1)} fps</dd>
          </div>
          <div>
            <dt class="text-neutral-500">Elapsed</dt>
            <dd class="text-neutral-200 text-sm">{(elapsedMs / 1000).toFixed(1)} s</dd>
          </div>
          <div>
            <dt class="text-neutral-500">USB drop</dt>
            <dd class="text-neutral-200 text-sm">{bytesDropped} B</dd>
          </div>
        </dl>
        <!-- Audio telemetry: lets us see whether the demod-to-speaker chain
             is actually alive. If "played" is 0 the worklet isn't running;
             if "ring" stays at 0 the DSP worker isn't writing; if "ring"
             grows without bound the worklet isn't reading. -->
        <dl
          class="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 rounded-md border border-neutral-800 bg-neutral-900 p-3 font-mono text-xs"
        >
          <div>
            <dt class="text-neutral-500">Audio</dt>
            <dd class="text-sm" class:text-emerald-400={audioWorkletReady} class:text-amber-400={!audioWorkletReady}>
              {audioWorkletReady ? 'worklet ready' : 'waiting'}
            </dd>
          </div>
          <div>
            <dt class="text-neutral-500">Played</dt>
            <dd class="text-(--color-accent) text-sm">{formatMSamples(audioSamplesPlayed)}</dd>
          </div>
          <div>
            <dt class="text-neutral-500">Ring</dt>
            <dd class="text-neutral-200 text-sm">{Math.round(audioRingUsed / 4)} S</dd>
          </div>
          <div>
            <dt class="text-neutral-500">Underrun</dt>
            <dd class="text-neutral-200 text-sm" class:text-amber-400={audioUnderrun > 100}>
              {formatMSamples(audioUnderrun)}
            </dd>
          </div>
        </dl>
      {/if}

      <div class="flex gap-2">
        {#if rtlStatus === 'connected'}
          <button
            type="button"
            onclick={onStart}
            class="inline-flex items-center gap-2 rounded-md bg-emerald-600 text-white px-4 py-2 text-sm font-medium hover:bg-emerald-500 cursor-pointer"
          >
            <Play size={16} />
            Start streaming
          </button>
        {:else if rtlStatus === 'streaming'}
          <button
            type="button"
            onclick={onStop}
            class="inline-flex items-center gap-2 rounded-md bg-amber-600 text-white px-4 py-2 text-sm font-medium hover:bg-amber-500 cursor-pointer"
          >
            <Square size={16} />
            Stop
          </button>
        {/if}
        <button
          type="button"
          onclick={onDisconnect}
          disabled={rtlStatus === 'closing'}
          class="inline-flex items-center gap-2 rounded-md border border-neutral-700 text-neutral-300 px-4 py-2 text-sm font-medium hover:border-neutral-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Unplug size={16} />
          Disconnect
        </button>
      </div>
    {:else if rtlStatus === 'error'}
      <div
        class="rounded-md border border-amber-700 bg-amber-950/30 p-3 mb-4 text-sm text-amber-300"
      >
        <div class="flex items-start gap-2">
          <CircleAlert size={16} class="mt-0.5 shrink-0" />
          <div>
            <p class="font-medium">Error</p>
            <p class="font-mono text-xs mt-1 break-words">{rtlError}</p>
          </div>
        </div>
      </div>
      <button
        type="button"
        onclick={() => {
          rtlStatus = 'idle';
          rtlError = null;
        }}
        class="inline-flex items-center gap-2 rounded-md border border-neutral-700 text-neutral-300 px-4 py-2 text-sm font-medium hover:border-neutral-500 cursor-pointer"
      >
        Reset
      </button>
    {/if}

    <p class="text-[10px] text-neutral-600 px-3 py-3 border-t border-neutral-800">
      v0.1.1 · Press <kbd class="font-mono text-neutral-400">?</kbd> for shortcuts ·
      <a
        href="https://github.com/matsvandamme/moshon-sdr/blob/main/AGENTS.md"
        class="underline decoration-dotted hover:text-(--color-accent)"
        target="_blank"
        rel="noreferrer">Roadmap</a
      >
    </p>
    </section>
    </aside>

    <!-- Main viewport — visualization + decode panels. -->
    <main class="flex-1 overflow-y-auto p-3 flex flex-col gap-3">
      {#if rtlStatus === 'idle'}
        <div class="flex-1 flex items-center justify-center text-neutral-500 text-sm font-mono">
          <p>← pick a source and click Connect</p>
        </div>
      {:else if rtlStatus === 'connecting'}
        <div class="flex-1 flex items-center justify-center gap-2 text-neutral-400 text-sm font-mono">
          <Loader2 size={16} class="animate-spin" />
          Waiting for device picker…
        </div>
      {:else if rtlStatus === 'connected' || rtlStatus === 'streaming' || rtlStatus === 'closing'}
        <!-- VFO + tuning summary. The dial is the big primary input;
             the readouts below keep mode / BW / step / gain in sight. -->
        <div class="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-end">
          <VfoDial
            centerFreq={tuning.centerFreq}
            stepSize={tuning.stepSize}
            onChange={(hz) => (tuning.centerFreq = hz)}
          />
          <dl class="grid grid-cols-4 lg:grid-cols-1 gap-x-4 gap-y-1 text-xs font-mono">
            <div class="lg:flex lg:items-baseline lg:gap-2">
              <dt class="text-neutral-500 uppercase">Mode</dt>
              <dd class="text-neutral-200">{MODE_INFO[tuning.mode].label}</dd>
            </div>
            <div class="lg:flex lg:items-baseline lg:gap-2">
              <dt class="text-neutral-500 uppercase">BW</dt>
              <dd class="text-neutral-200">{formatHz(tuning.bandwidth)}</dd>
            </div>
            <div class="lg:flex lg:items-baseline lg:gap-2">
              <dt class="text-neutral-500 uppercase">Step</dt>
              <dd class="text-neutral-200">{formatHz(tuning.stepSize)}</dd>
            </div>
            <div class="lg:flex lg:items-baseline lg:gap-2">
              <dt class="text-neutral-500 uppercase">Gain</dt>
              <dd class="text-neutral-200">
                {#if inputMode === 'hackrf'}
                  {hrfAmpOn ? `+${HACKRF_AMP_DB}` : '–'} / {hrfLnaDb} / {hrfVgaDb} dB
                {:else}
                  {gainLabel(tuning.gain)}
                {/if}
              </dd>
            </div>
          </dl>
        </div>

        {#if sweepOpen}
          <div>
            <SweepPanel
              sampleRate={sampleRate}
              latestBins={latestBins}
              onRetune={onSweepRetune}
              onCancel={closeSweep}
              onPickFrequency={onSweepPick}
            />
            <button
              type="button"
              onclick={closeSweep}
              class="mt-2 text-xs text-neutral-500 hover:text-neutral-200 font-mono cursor-pointer underline decoration-dotted"
            >
              ← back to live spectrum
            </button>
          </div>
        {/if}

        <div class="rounded-md overflow-hidden border border-neutral-800 bg-black">
          <SpectrumWaterfall
            bins={latestBins}
            centerFreq={tuning.centerFreq}
            sampleRate={sampleRate}
            {dbMin}
            {dbMax}
            {colormap}
            stepSize={tuning.stepSize}
            onTune={onClickToTune}
          />
        </div>

        <!-- S-meter -->
        {#if rtlStatus === 'streaming' && Number.isFinite(signalDb)}
          <div
            class="flex items-center gap-3 rounded-md border border-neutral-800 bg-neutral-900 px-3 py-2 font-mono text-xs"
            aria-label="Signal strength"
          >
            <span class="text-neutral-500 uppercase">Signal</span>
            <span class="text-(--color-accent) text-sm tabular-nums">
              S{signalS.sNumber}{#if signalS.plus > 0}+{signalS.plus}{/if}
            </span>
            <span class="text-neutral-400 tabular-nums">
              {signalDb.toFixed(1)} dBFS
            </span>
            <div class="flex-1 h-1.5 rounded bg-neutral-800 overflow-hidden">
              <div
                class="h-full bg-(--color-accent)"
                style="width: {Math.max(0, Math.min(100, ((signalDb + 100) / 100) * 100))}%"
              ></div>
            </div>
          </div>
        {/if}

        <!-- Mode-specific decode panels. Each only renders when the
             corresponding mode is selected AND we have meaningful data. -->
        {#if tuning.mode === 'adsb' && rtlStatus === 'streaming'}
          <AircraftPanel />
        {/if}

        {#if tuning.mode === 'lora' && rtlStatus === 'streaming'}
          <LoraPanel channelDb={signalDb} />
        {/if}

        {#if tuning.mode === 'wfm' && rtlStatus === 'streaming' && (rdsSynced || rdsStereo)}
          <div
            class="rounded-md border border-neutral-800 bg-neutral-950 p-3 font-mono text-xs"
            aria-label="RDS"
          >
            <div class="flex items-center justify-between mb-1.5 text-neutral-500 uppercase">
              <span class="flex items-center gap-2">
                <span>RDS</span>
                {#if rdsSynced}
                  <span class="text-emerald-400 text-[10px]">locked</span>
                {:else}
                  <span class="text-neutral-600 text-[10px]">searching…</span>
                {/if}
                {#if rdsStereo}
                  <span class="text-(--color-accent) text-[10px]">stereo</span>
                {/if}
              </span>
              {#if rdsSynced && rdsPi > 0}
                <span class="tabular-nums text-neutral-400">
                  PI 0x{rdsPi.toString(16).toUpperCase().padStart(4, '0')}
                </span>
              {/if}
            </div>
            {#if rdsSynced}
              <div class="flex items-baseline gap-2 mb-1">
                <span class="text-neutral-500 text-[10px] uppercase w-6">PS</span>
                <span class="text-(--color-accent) text-base tracking-wide">{rdsPs}</span>
              </div>
              {#if rdsRt.trim().length > 0}
                <div class="flex items-start gap-2">
                  <span class="text-neutral-500 text-[10px] uppercase w-6 mt-0.5">RT</span>
                  <span class="text-neutral-300 break-words flex-1">{rdsRt}</span>
                </div>
              {/if}
            {/if}
          </div>
        {/if}

        {#if tuning.mode === 'cw' && rtlStatus === 'streaming'}
          <div
            class="rounded-md border border-neutral-800 bg-neutral-950 p-3 font-mono text-xs"
            aria-label="CW decode"
          >
            <div class="flex items-center justify-between mb-1.5 text-neutral-500 uppercase">
              <span>CW decode</span>
              <span class="tabular-nums">
                {cwDecodedWpm > 0 ? `~${cwDecodedWpm.toFixed(0)} WPM` : ''}
                <button
                  type="button"
                  onclick={() => (cwDecodedText = '')}
                  class="ml-2 text-neutral-600 hover:text-neutral-200 cursor-pointer"
                  title="Clear decoded text"
                >clear</button>
              </span>
            </div>
            <div
              class="max-h-24 overflow-y-auto text-(--color-accent) leading-relaxed whitespace-pre-wrap break-words"
            >
              {cwDecodedText || '…listening…'}
            </div>
          </div>
        {/if}
      {:else if rtlStatus === 'error'}
        <div class="flex-1 flex items-center justify-center text-rose-400 text-sm font-mono">
          {rtlError}
        </div>
      {/if}
    </main>
  </div>
</div>
